
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';
import LinkTokenCreate from 'src/components/docs/LinkTokenCreate';
import Layout from '../../../components/docs/Layout';
export const metadata = {
  toc: true,
  layout: 'guide',
  description: 'Learn how to migrate from Bank Income to CRA Income Insights'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const Callout = makeShortcode("Callout");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <Header title='Migrate from Bank Income to Plaid Consumer Report' subtitle='Switch to CRA Income Insights for model-driven income attributes and FCRA compliance' mdxType="Header" />
    <p>{`This guide will walk you through how to migrate from accessing bank data with Plaid's `}<a parentName="p" {...{
        "href": "/docs/income/"
      }}>{`Bank Income`}</a>{` product to generating Income Insights Reports with Plaid Check's Consumer Report.`}</p>
    <p>{`The CRA Income Insights Report offers model-driven attributes such as historical gross and net income, facilitating streamlined debt-to-income calculations and income verification. It enables assessment of financial stability through insights like forecasted income, employer name, income frequency, and predicted next payment date.`}</p>
    <h4 {...{
      "id": "advantages-of-plaid-consumer-report"
    }}>{`Advantages of Plaid Consumer Report`}</h4>
    <p>{`Bank Income gives you net income data from both irregular or gig income and W-2 income. The CRA Income Insights Report enhances this by:`}</p>
    <ul>
      <li parentName="ul">{`Providing FCRA-compliant insights  `}</li>
      <li parentName="ul">{`Bundling income calculations (historical average income, forecasted income, predicted next payment date) in a single report  `}</li>
    </ul>
    <p>{`Upgrading to Plaid Consumer Report is recommended for all Bank Income customers whose end users are based in the US. (CRA Income Insights is not available for end users outside the US.)`}</p>
    <Callout mdxType="Callout">
      <p>{`Income Insights does not replace Document Income or Payroll Income. Plaid Check products can't be used in the same Link flow as Plaid Inc. credit products such as Plaid Document Income, Payroll Income, Assets, or Statements. If you want to continue using any of those products, create separate `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{` requests: one for Plaid Check products, and one for the Plaid Inc. credit products.`}</p>
      <p>{`Non-credit Plaid products, such as Balance and Auth, can continue to be used in the same Link flow as Plaid Check products, and do not require any special changes to your integration.`}</p>
    </Callout>
    <h4 {...{
      "id": "prerequisites"
    }}>{`Prerequisites`}</h4>
    <ol>
      <li parentName="ol">
        <p parentName="li">{`To use Consumer Report, it is strongly recommended to update your Plaid client library to the latest version. The minimum required versions for new Consumer Report integrations are:`}</p>
        <ul parentName="li">
          <li parentName="ul">{`Python: 38.0.0`}</li>
          <li parentName="ul">{`Go: 41.0.0`}</li>
          <li parentName="ul">{`Java: 39.0.0`}</li>
          <li parentName="ul">{`Node: 41.0.0`}</li>
          <li parentName="ul">{`Ruby: 45.0.0`}</li>
        </ul>
      </li>
      <li parentName="ol">
        <p parentName="li"><a parentName="p" {...{
            "href": "https://dashboard.plaid.com/settings/team/products"
          }}>{`Confirm`}</a>{` that you have access to all required Plaid Check products in the Production environment. If you don't have access to Plaid Check, `}<a parentName="p" {...{
            "href": "https://dashboard.plaid.com/overview/request-products"
          }}>{`request access via the Dashboard`}</a>{`. In order to use Plaid Check, your end users must be in the US.`}</p>
      </li>
    </ol>
    <h4 {...{
      "id": "changes-to-plaid-link-initialization"
    }}>{`Changes to Plaid Link initialization`}</h4>
    <p>{`When using Plaid Check products, you must create a user prior to sending the user through Link and initialize Link with the resulting `}<inlineCode parentName="p">{`user_id`}</inlineCode>{`. This allows Plaid to associate multiple Items with a single user.`}</p>
    <ol>
      <li parentName="ol">
        <p parentName="li">{`Call `}<inlineCode parentName="p">{`/user/create`}</inlineCode>{` prior to creating a Link token:  `}</p>
        <ul parentName="li">
          <li parentName="ul">{`Include identity info in the `}<inlineCode parentName="li">{`identity`}</inlineCode>{` object. At minimum, the following fields must be provided: `}<inlineCode parentName="li">{`name`}</inlineCode>{`, `}<inlineCode parentName="li">{`date_of_birth`}</inlineCode>{`, `}<inlineCode parentName="li">{`emails`}</inlineCode>{`, `}<inlineCode parentName="li">{`phone_numbers`}</inlineCode>{`, and `}<inlineCode parentName="li">{`addresses`}</inlineCode>{` (with at least one email address, phone number, and address designated as `}<inlineCode parentName="li">{`primary`}</inlineCode>{`). If you intend to share the report with a GSE (Government-Sponsored Enterprise) such as Fannie or Freddie, the full SSN is also required via the `}<inlineCode parentName="li">{`id_numbers`}</inlineCode>{` field. For all use cases, providing at least a partial SSN is highly recommended, since it improves the accuracy of matching user records during compliance processes such as file disclosure, dispute, or security freeze requests.`}</li>
          <li parentName="ul">{`Store the `}<inlineCode parentName="li">{`user_id`}</inlineCode>{` in your database.`}</li>
        </ul>
      </li>
      <li parentName="ol">
        <p parentName="li">{`Update your `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{` request:  `}</p>
        <ul parentName="li">
          <li parentName="ul">{`Include the `}<inlineCode parentName="li">{`user_id`}</inlineCode>{` string from `}<inlineCode parentName="li">{`/user/create`}</inlineCode>{`.`}</li>
          <li parentName="ul">{`In the `}<inlineCode parentName="li">{`products`}</inlineCode>{` array, replace `}<inlineCode parentName="li">{`income_verification`}</inlineCode>{` with `}<inlineCode parentName="li">{`cra_base_report`}</inlineCode>{` and `}<inlineCode parentName="li">{`cra_income_insights`}</inlineCode>{`.`}</li>
          <li parentName="ul">{`Remove the `}<inlineCode parentName="li">{`income_verification`}</inlineCode>{` object.  `}</li>
          <li parentName="ul">{`Add a `}<inlineCode parentName="li">{`cra_options`}</inlineCode>{` object and specify `}<inlineCode parentName="li">{`days_requested`}</inlineCode>{` (minimum 180).`}</li>
          <li parentName="ul">{`If you haven't already, set `}<inlineCode parentName="li">{`webhook`}</inlineCode>{` to your webhook endpoint listener URL. Unlike Bank Income, Plaid Check requires the use of webhooks.`}</li>
          <li parentName="ul">{`Provide a `}<inlineCode parentName="li">{`consumer_report_permissible_purpose`}</inlineCode></li>
          <li parentName="ul">{`(Optional) To allow linking multiple Items in one session, set `}<inlineCode parentName="li">{`enable_multi_item_link`}</inlineCode>{` to `}<inlineCode parentName="li">{`true`}</inlineCode>{`.`}</li>
        </ul>
      </li>
    </ol>
    <LinkTokenCreate useCases={[{
      useCase: 'Income Insights (Consumer Report by Plaid Check)',
      comment: 'Generate the user_id by calling /user/create',
      config: {
        user: {
          client_user_id: 'user-abc',
          email_address: 'user@example.com'
        },
        user_id: 'usr_9nSp2KuZ2x4JDw',
        products: ['cra_income_insights', 'cra_base_report'],
        consumer_report_permissible_purpose: 'EXTENSION_OF_CREDIT',
        enable_multi_item_link: true,
        cra_options: {
          days_requested: 365
        },
        client_name: 'The Loan Arranger',
        language: 'en',
        country_codes: ['US'],
        webhook: 'https://sample-web-hook.com'
      }
    }]} filenameAsTitle={true} mdxType="LinkTokenCreate" />
    <p>{`For more details, see the Plaid Check `}<a parentName="p" {...{
        "href": "/docs/check/add-to-app/"
      }}>{`Implementation guide`}</a>{`.`}</p>
    <h5 {...{
      "id": "adding-consumer-report-to-existing-items"
    }}>{`Adding Consumer Report to existing Items`}</h5>
    <p>{`To enable an existing Income-enabled Item for Consumer Report, call `}<inlineCode parentName="p">{`/user/create`}</inlineCode>{` and `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{` as described above, but include the Item's `}<inlineCode parentName="p">{`access_token`}</inlineCode>{` when calling `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{`. When Link is launched, the end user will go through the Consumer Report consent flow, and on successful completion of the flow, the Item will be enabled for Consumer Report.`}</p>
    <h4 {...{
      "id": "update-product-api-calls-and-webhook-listeners"
    }}>{`Update product API calls and webhook listeners`}</h4>
    <p>{`Unlike Bank Income, Plaid Check uses webhooks with async report generation. `}</p>
    <ol>
      <li parentName="ol">
        <p parentName="li">{`Add webhook listener endpoints for `}<a parentName="p" {...{
            "href": "/docs/api/products/check-legacy/#user_check_report_ready"
          }}><inlineCode parentName="a">{`USER_CHECK_REPORT_READY`}</inlineCode></a>{` and `}<a parentName="p" {...{
            "href": "/docs/api/products/check-legacy/#user_check_report_failed"
          }}><inlineCode parentName="a">{`USER_CHECK_REPORT_FAILED`}</inlineCode></a>{`.`}</p>
        <ul parentName="li">
          <li parentName="ul">{`Upon receiving the `}<a parentName="li" {...{
              "href": "/docs/api/products/check-legacy/#user_check_report_ready"
            }}><inlineCode parentName="a">{`USER_CHECK_REPORT_READY`}</inlineCode></a>{` webhook, call product endpoints such as `}<inlineCode parentName="li">{`/cra/check_report/income_insights/get`}</inlineCode>{`.`}</li>
          <li parentName="ul">{`Upon receiving the `}<a parentName="li" {...{
              "href": "/docs/api/products/check-legacy/#user_check_report_failed"
            }}><inlineCode parentName="a">{`USER_CHECK_REPORT_FAILED`}</inlineCode></a>{` webhook, call `}<inlineCode parentName="li">{`/user/items/get`}</inlineCode>{` to determine why Items are in a bad state. If appropriate, send the user through `}<a parentName="li" {...{
              "href": "/docs/link/update-mode"
            }}>{`update mode`}</a>{` to repair the Item.`}</li>
        </ul>
      </li>
      <li parentName="ol">
        <p parentName="li">{`Replace any Income endpoints and webhook listeners with the equivalent Plaid Check endpoint or webhook listener, using the `}<a parentName="p" {...{
            "href": "[redacted]##########################################################################################################"
          }}>{`API response comparison sheet`}</a>{` to map response fields between the old endpoints and new endpoints.`}</p>
      </li>
    </ol>
    <Layout.FullWidth>
      <table>
        <thead parentName="table">
          <tr parentName="thead">
            <th parentName="tr" {...{
              "align": null
            }}>{`Income endpoint`}</th>
            <th parentName="tr" {...{
              "align": null
            }}>{`Equivalent Plaid Check endpoint`}</th>
          </tr>
        </thead>
        <tbody parentName="table">
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`/credit/sessions/get`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`None (remove)`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`/credit/bank_income/get`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`/cra/check_report/income_insights/get`}</inlineCode>{` and/or `}<inlineCode parentName="td">{`/cra/check_report/base_report/get`}</inlineCode></td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`/credit/bank_income/pdf/get`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`/cra/check_report/pdf/get`}</inlineCode>{` with `}<inlineCode parentName="td">{`add_ons: ["cra_income_insights"]`}</inlineCode></td>
          </tr>
        </tbody>
      </table>
    </Layout.FullWidth>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Income webhook`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Equivalent Plaid Check webhook`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`USER_INCOME_VERIFICATION`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`USER_CHECK_REPORT_READY`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`INCOME_VERIFICATION`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`USER_CHECK_REPORT_READY`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`BANK_INCOME_REFRESH_COMPLETE`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`USER_CHECK_REPORT_READY`}</inlineCode></td>
        </tr>
      </tbody>
    </table>
    <h4 {...{
      "id": "api-response-comparison"
    }}>{`API response comparison`}</h4>
    <p>{`This `}<a parentName="p" {...{
        "href": "[redacted]##########################################################################################################"
      }}>{`Google Sheet`}</a>{` highlights the correspondences between Bank Income and Plaid Check Income Insights schemas.`}</p>
    <p>{`If you are using other Plaid Inc. products, note that the `}<inlineCode parentName="p">{`account_id`}</inlineCode>{` returned in API responses from endpoints prefixed with `}<inlineCode parentName="p">{`/cra/`}</inlineCode>{` will not match the `}<inlineCode parentName="p">{`account_id`}</inlineCode>{` returned in responses from non-CRA Plaid endpoints.`}</p>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "advantages-of-plaid-consumer-report",
        level: 4,
        title: "Advantages of Plaid Consumer Report",
      },
{
        id: "prerequisites",
        level: 4,
        title: "Prerequisites",
      },
{
        id: "changes-to-plaid-link-initialization",
        level: 4,
        title: "Changes to Plaid Link initialization",
      },
{
        id: "update-product-api-calls-and-webhook-listeners",
        level: 4,
        title: "Update product API calls and webhook listeners",
      },
{
        id: "api-response-comparison",
        level: 4,
        title: "API response comparison",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
